import type { Zone, ZoneCause, ZoneRemedy } from './zones';
import { type Lang, type UIStrings, getCurrentLang, setLang, t } from './i18n';

const $ = <T extends HTMLElement>(id: string): T =>
    document.getElementById(id) as T;

// DOM refs
const panel = $<HTMLElement>('info-panel');
const panelContent = $<HTMLElement>('panel-content');
const panelClose = $<HTMLButtonElement>('panel-close');
const disclaimerModal = $<HTMLElement>('disclaimer-modal');
const btnAccept = $<HTMLButtonElement>('btn-accept-disclaimer');
const btnShowDisclaimer = $<HTMLButtonElement>('btn-show-disclaimer');
const interactionHint = $<HTMLElement>('interaction-hint');
const btnWholeHead = $<HTMLButtonElement>('btn-whole-head');
const btnLang = $<HTMLButtonElement>('btn-lang');
const langLabel = $<HTMLElement>('lang-label');

// --- Multi-select state ---
const selectedZones = new Map<string, Zone>();
let lang: Lang = getCurrentLang();
let strings: UIStrings = t(lang);

function l(obj: { ru: string; en: string }): string {
    return obj[lang];
}

// --- Card generators ---
function createCauseCard(cause: ZoneCause): string {
    const sevKey = `badge${cause.severity.charAt(0).toUpperCase() + cause.severity.slice(1)}` as keyof UIStrings;
    const sevText = strings[sevKey] as string;
    const cls = `badge-${cause.severity}`;
    return `<div class="cause-card">
        <div class="card-header">
            <span class="card-title">${l(cause.title)}</span>
            <span class="badge ${cls}">${sevText}</span>
        </div>
        <p class="card-description">${l(cause.description)}</p>
    </div>`;
}

function createRemedyCard(remedy: ZoneRemedy): string {
    const typeKey = `badge${remedy.type.charAt(0).toUpperCase() + remedy.type.slice(1)}` as keyof UIStrings;
    const typeText = strings[typeKey] as string;
    const cls = `badge-${remedy.type}`;
    return `<div class="remedy-card">
        <div class="card-header">
            <span class="card-title">${l(remedy.title)}</span>
            <span class="badge ${cls}">${typeText}</span>
        </div>
        <p class="card-description">${l(remedy.description)}</p>
    </div>`;
}

// --- Render a single zone block ---
function renderZoneBlock(zone: Zone): string {
    return `<div class="zone-block">
        <div class="panel-header">
            <h2 class="zone-name">${l(zone.name)}</h2>
        </div>
        <p class="zone-description">${l(zone.description)}</p>
        <section class="panel-section">
            <h3>${strings.causesTitle}</h3>
            <div>${zone.causes.map(createCauseCard).join('')}</div>
        </section>
        <section class="panel-section">
            <h3>${strings.remediesTitle}</h3>
            <div>${zone.remedies.map(createRemedyCard).join('')}</div>
        </section>
        <section class="panel-section panel-warning">
            <h3>${strings.doctorTitle}</h3>
            <p>${l(zone.whenToSeeDoctor)}</p>
        </section>
    </div>`;
}

// --- Render all selected zones into panel ---
function renderPanel(): void {
    const zones = Array.from(selectedZones.values());
    panelContent.innerHTML = zones
        .map((z, i) => {
            const block = renderZoneBlock(z);
            return i < zones.length - 1
                ? block + '<hr class="zone-divider">'
                : block;
        })
        .join('');
}

// --- Public API ---

/** Toggle a zone in/out of the selection. Returns true if zone is now selected. */
export function toggleZone(zone: Zone): boolean {
    if (selectedZones.has(zone.id)) {
        selectedZones.delete(zone.id);
    } else {
        selectedZones.set(zone.id, zone);
    }

    if (selectedZones.size === 0) {
        panel.classList.remove('active');
        return false;
    }

    renderPanel();
    panel.classList.add('active');
    hideHint();

    // Scroll to top only when adding the first zone
    if (selectedZones.size === 1) {
        panel.scrollTop = 0;
    }

    return selectedZones.has(zone.id);
}

/** Callback to notify app.ts when selection changes (e.g., to reset 3D highlights) */
let onSelectionClearedCallback: (() => void) | null = null;

export function onSelectionCleared(cb: () => void): void {
    onSelectionClearedCallback = cb;
}

/** Clear all selected zones and hide panel */
export function clearSelection(): void {
    selectedZones.clear();
    panel.classList.remove('active');
    onSelectionClearedCallback?.();
}

/** Get IDs of all currently selected zones */
export function getSelectedZoneIds(): string[] {
    return Array.from(selectedZones.keys());
}

/** Check if a specific zone is selected */
export function isZoneSelected(zoneId: string): boolean {
    return selectedZones.has(zoneId);
}

function hideHint(): void {
    interactionHint.classList.add('hidden');
}

function showDisclaimer(): void {
    disclaimerModal.classList.remove('hidden');
}

function hideDisclaimer(): void {
    disclaimerModal.classList.add('hidden');
    localStorage.setItem('headache-maps-disclaimer', '1');
}

/** Apply all UI strings for current language */
function applyLang(): void {
    strings = t(lang);
    document.documentElement.lang = lang;
    document.title = strings.pageTitle;

    $('header-title').textContent = strings.headerTitle;
    $('disclaimer-btn-text').textContent = strings.disclaimer;
    $('hint-main').innerHTML = strings.hintMain;
    $('hint-sub').textContent = strings.hintSub;
    btnWholeHead.textContent = strings.wholeHeadBtn;
    $('disclaimer-title').textContent = strings.disclaimerTitle;
    $('disclaimer-p1').innerHTML = strings.disclaimerP1;
    $('disclaimer-p2').innerHTML = strings.disclaimerP2;
    $('disclaimer-sources').textContent = strings.disclaimerSources;
    btnAccept.textContent = strings.disclaimerAccept;

    // Loading text
    const loadingText = document.getElementById('loading-text');
    if (loadingText) loadingText.textContent = strings.loading;

    // Show opposite lang as toggle label
    langLabel.textContent = lang === 'ru' ? 'EN' : 'RU';

    // Re-render open panel if any zones selected
    if (selectedZones.size > 0 && panel.classList.contains('active')) {
        renderPanel();
    }
}

function toggleLang(): void {
    lang = lang === 'ru' ? 'en' : 'ru';
    setLang(lang);
    applyLang();
}

export function initUI(zones: Zone[]): void {
    // Language
    applyLang();
    btnLang.addEventListener('click', toggleLang);

    // Panel close
    panelClose.addEventListener('click', () => clearSelection());
    panel.addEventListener('click', (e) => {
        if (e.target === panel) clearSelection();
    });

    // Disclaimer
    btnAccept.addEventListener('click', hideDisclaimer);
    btnShowDisclaimer.addEventListener('click', showDisclaimer);

    if (!localStorage.getItem('headache-maps-disclaimer')) {
        showDisclaimer();
    } else {
        hideDisclaimer();
    }

    // "Whole head" button — clears 3D selection and shows whole_head info
    const wholeHeadZone = zones.find((z) => z.id === 'whole_head');
    if (wholeHeadZone) {
        btnWholeHead.addEventListener('click', () => {
            selectedZones.clear();
            onSelectionClearedCallback?.(); // Reset 3D highlights
            selectedZones.set(wholeHeadZone.id, wholeHeadZone);
            renderPanel();
            panel.classList.add('active');
            panel.scrollTop = 0;
            hideHint();
        });
    }

    // Escape key
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
            if (!disclaimerModal.classList.contains('hidden')) {
                hideDisclaimer();
            } else if (panel.classList.contains('active')) {
                clearSelection();
            }
        }
    });
}
